/**
 * Procedural face (TGSL): crisp signed-distance features in face space, see face.ts for the parameters.
 * `faceErase` replaces the painted features of the base texture with fur colours; `faceFeatures` returns the inked
 * features (rgb + coverage) composited after lighting.
 */
import tgpu from 'typegpu';
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';

export const FaceParams = d.struct({
  eye: d.vec4f,     // spacing, y, size, aspect
  eye2: d.vec4f,    // tilt, irisSize, pupilSize, pupilSlit
  iris: d.vec4f,    // rgb, bags
  gaze: d.vec4f,    // gx, gy, lidClose, —
  lid: d.vec4f,     // lidAngle, lowerLid, derp, —
  brow: d.vec4f,    // y, angle, thickness, curve
  brow2: d.vec4f,   // length, noseY, noseSize, whiskerDots
  mouth: d.vec4f,   // y, width, open, round
  mouth2: d.vec4f,  // smile, fangs, tongue, blush
  misc: d.vec4f,    // tear, sweat, enabled, debug
  skinUv: d.vec4f,  // cheek uv, muzzle uv
  nose: d.vec4f,    // rgb, eraseRadius
  erase: d.vec4f,   // eraseY, time, preserve natural muzzle, —
});
export const faceLayout = tgpu.bindGroupLayout({ face: { uniform: FaceParams } });

const INK = d.vec3f(0.13, 0.09, 0.07);

const pigmentHash = (p: d.v2f): number => {
  'use gpu';
  return std.fract(std.sin(std.dot(p, d.vec2f(127.1, 311.7))) * 43758.5453);
};
const pigment = (p: d.v2f): number => {
  'use gpu';
  const cell = std.floor(p);
  const f = std.fract(p);
  const u = f.mul(f).mul(d.vec2f(3).sub(f.mul(2)));
  return std.mix(std.mix(pigmentHash(cell), pigmentHash(cell.add(d.vec2f(1, 0))), u.x), std.mix(pigmentHash(cell.add(d.vec2f(0, 1))), pigmentHash(cell.add(d.vec2f(1))), u.x), u.y);
};

const cover = (dist: number, aa: number): number => {
  'use gpu';
  return 1.0 - std.smoothstep(-aa, aa, dist);
};
const strokeA = (dist: number, w: number, aa: number): number => {
  'use gpu';
  return 1.0 - std.smoothstep(w - aa, w + aa, std.abs(dist));
};
const rot = (p: d.v2f, a: number): d.v2f => {
  'use gpu';
  const c = std.cos(a);
  const s = std.sin(a);
  return d.vec2f(p.x * c - p.y * s, p.x * s + p.y * c);
};
/** Approximate signed distance to an axis-aligned ellipse. */
const ellipse = (p: d.v2f, r: d.v2f): number => {
  'use gpu';
  return (std.length(p.div(r)) - 1.0) * std.min(r.x, r.y);
};

/** Mask (0..1) of the region whose painted features are erased, and its fur colour mix factor (top → bottom). */
export const faceEraseMask = (uv: d.v2f): number => {
  'use gpu';
  const fp = faceLayout.$.face;
  const r = fp.nose.w;
  if (fp.erase.z > 0.5) {
    const eyeMask = std.length(d.vec2f(std.abs(uv.x) - 0.5, uv.y - 0.04).div(d.vec2f(0.51 * r, 0.51 * r)));
    const browMask = std.length(d.vec2f(std.abs(uv.x) - 0.34, uv.y - 0.73).div(d.vec2f(0.27 * r, 0.25 * r)));
    const mouthCenter=-.62+fp.mouth.x;
    const mouthMask=std.length(uv.sub(d.vec2f(0,mouthCenter-.055)).div(d.vec2f(std.max(.29,fp.mouth.y*.7),.18)));
    const mouthClean=(1-std.smoothstep(.8,1.4,mouthMask))*(1-std.smoothstep(mouthCenter+.08,mouthCenter+.15,uv.y));
    return std.max(mouthClean,std.max(1.0 - std.smoothstep(0.98, 1.55, eyeMask), 1.0 - std.smoothstep(0.65, 1.35, browMask)));
  }
  const eyeMask = std.length(d.vec2f(std.abs(uv.x) - 0.5, uv.y - 0.02).div(d.vec2f(0.45 * r, 0.4 * r)));
  const browMask = std.length(d.vec2f(std.abs(uv.x) - 0.5, uv.y - 0.5).div(d.vec2f(0.35 * r, 0.18 * r)));
  const muzzleY = -0.49 + (fp.brow2.y + fp.mouth.x) * 0.5;
  const muzzleMask = std.length(uv.sub(d.vec2f(0, muzzleY + fp.erase.x * 0.2)).div(d.vec2f(0.52 * r, 0.39 * r)));
  // Clean the source features, rather than washing the entire skull into a flat
  // oval. Fur between the eye regions and around the cheeks remains authored.
  return 1.0 - std.smoothstep(0.92, 1.4, std.min(std.min(eyeMask, muzzleMask), browMask));
};

/** One eye (side = +1 character's left, −1 right): returns rgb + coverage over the current colour. */
const eyeLayer = (uv: d.v2f, side: number, aa: number, acc: d.v4f): d.v4f => {
  'use gpu';
  const fp = faceLayout.$.face;
  const spacing = fp.eye.x;
  const derp = std.select(d.f32(1), 1.0 + fp.lid.z * 0.35, side < 0.0);
  const R = fp.eye.z * derp;
  const H = R * fp.eye.w;
  const c = d.vec2f(side * 0.5 * spacing, fp.eye.y);
  const q0 = rot(uv.sub(c), side * fp.eye2.x);
  const q = d.vec2f(q0.x * side, q0.y); // mirror so +x is always the outer corner
  const inner = std.clamp(-q.x / R, -1.0, 1.0);
  const close = fp.gaze.z;
  // lids: the upper lid slants with the emotion (angry: inner corner down), droops at the ends
  const yUp = H * (1.0 - 2.0 * close) - fp.lid.x * H * 0.55 * inner - H * 0.22 * (q.x / R) * (q.x / R);
  const yLo = -H + fp.lid.y * H * 1.1 + H * 0.12 * (q.x / R) * (q.x / R);
  const shape = ellipse(q, d.vec2f(R, H));
  const open = std.max(shape, std.max(q.y - yUp, yLo - q.y));
  let col = acc.xyz;
  let a = acc.w;
  // sclera
  const sclera = cover(open, aa);
  const paper = 0.98 + pigment(uv.mul(95)) * 0.035 * (1.0 - std.smoothstep(0.015, 0.05, aa));
  const curvature = 0.86 + 0.14 * std.sqrt(std.max(0, 1.0 - (q.x / R) * (q.x / R)));
  const lidShade = std.mix(d.f32(0.81), d.f32(1), std.smoothstep(0.0, H * 0.5, yUp - q.y));
  const scleraColor = std.mix(d.vec3f(0.7, 0.64, 0.5), d.vec3f(0.97, 0.94, 0.83), 1.0 - std.smoothstep(yLo, yUp + H * 0.4, q.y)).mul(curvature * lidShade * paper);
  col = std.mix(col, scleraColor, sclera);
  a = std.max(a, sclera);
  // iris + pupil (gaze moves them; clipped by the open lids)
  const g = d.vec2f(fp.gaze.x * R * 0.42 * side, fp.gaze.y * H * 0.35);
  const ir = fp.eye2.y * H * 1.05;
  const irisD = std.length(q.sub(g)) - ir;
  const irisA = cover(std.max(irisD, open), aa);
  const irisQ = q.sub(g);
  const angle = std.atan2(irisQ.y, irisQ.x);
  const radial = std.length(irisQ) / ir;
  const fibres = std.sin(angle * 23.0 + std.sin(angle * 11.0) * 1.5 + radial * 6.0) + std.sin(angle * 47.0 + radial * 9.0) * 0.3;
  const irisPigment = 0.96 + fibres * 0.035 + pigment(uv.mul(140)) * 0.04;
  const irisCol = std.mix(fp.iris.xyz.mul(0.59), fp.iris.xyz, 1.0 - std.smoothstep(ir * 0.25, ir, std.length(q.sub(g).sub(d.vec2f(0, ir * 0.25))))).mul(irisPigment);
  col = std.mix(col, irisCol, irisA);
  col = std.mix(col, INK, strokeA(irisD, aa * 0.6, aa) * cover(open, aa) * 0.7);
  const pw = ir * fp.eye2.z * (1.0 - 0.78 * fp.eye2.w);
  const ph = ir * std.min(0.96, fp.eye2.z * (1.0 + 0.9 * fp.eye2.w));
  const pupilA = cover(std.max(ellipse(q.sub(g), d.vec2f(std.max(pw, 0.012), ph)), open), aa);
  col = std.mix(col, d.vec3f(0.05, 0.04, 0.04), pupilA);
  const hlA = cover(std.max(std.length(q.sub(g).sub(d.vec2f(-ir * 0.32, ir * 0.06))) - ir * 0.17, open), aa);
  col = std.mix(col, d.vec3f(1, 0.98, 0.92), hlA * 0.95);
  // upper lid ink (thick) across the eye width; it becomes the closed-eye line
  const span = 1.0 - std.smoothstep(R * 0.92, R * 1.04, std.abs(q.x));
  const lidLine = strokeA(q.y - std.max(yUp, yLo), 0.018 + 0.009 * (1.0 - close), aa) * span;
  col = std.mix(col, INK, lidLine);
  a = std.max(a, lidLine);
  // the crease of a heavy upper lid (the tired, human look of medieval cats)
  const crease = strokeA(q.y - (H * 1.05 + H * 0.35 * (1.0 - close) - H * 0.25 * (q.x / R) * (q.x / R) - fp.lid.x * H * 0.3 * inner), 0.008, aa) * (1.0 - std.smoothstep(R * 0.75, R * 0.95, std.abs(q.x))) * fp.iris.w * 0.1;
  col = std.mix(col, INK, crease);
  a = std.max(a, crease);
  // lower lid (thin)
  const lowLine = strokeA(q.y - yLo, 0.011, aa) * span * (1.0 - close) * 0.8;
  col = std.mix(col, INK, lowLine);
  a = std.max(a, lowLine);
  // outer corner flick (medieval eyeliner)
  const flick = strokeA(q.y - (H * 0.15 + (q.x - R) * 0.35), 0.012, aa) * std.smoothstep(R * 0.9, R, q.x) * (1.0 - std.smoothstep(R * 1.12, R * 1.18, q.x));
  col = std.mix(col, INK, flick);
  a = std.max(a, flick);
  // eye bags: two thin arcs below
  const bagD = std.abs(std.length(q.sub(d.vec2f(0, -H * 0.1))) - H * 1.35) - 0.006;
  const bag1 = cover(bagD, aa) * (1.0 - std.smoothstep(-H * 1.1, -H * 0.85, q.y)) * (1.0 - std.smoothstep(R * 0.55, R * 0.8, std.abs(q.x))) * fp.iris.w * 0.75;
  col = std.mix(col, INK, bag1);
  a = std.max(a, bag1);
  return d.vec4f(col, a);
};

const browLayer = (uv: d.v2f, side: number, aa: number, acc: d.v4f): d.v4f => {
  'use gpu';
  const fp = faceLayout.$.face;
  const len = fp.brow2.x;
  const H = fp.eye.z * fp.eye.w;
  const xi = 0.5 * fp.eye.x - len * 0.45;
  const xo = 0.5 * fp.eye.x + len * 0.55;
  const x = uv.x * side;
  const t = (x - xi) / (xo - xi);
  const yb = fp.eye.y + H * 1.55 + 0.06 + fp.brow.x;
  const ang = fp.brow.y;
  const y = std.mix(yb - ang * 0.11, yb + ang * 0.11, t) + fp.brow.w * 0.07 * (1.0 - (2.0 * t - 1.0) * (2.0 * t - 1.0));
  const w = fp.brow.z * (1.0 - 0.55 * std.clamp(t, 0.0, 1.0));
  const inside = std.smoothstep(-0.06, 0.0, t) * (1.0 - std.smoothstep(1.0, 1.06, t));
  const b = strokeA(uv.y - y, w, aa) * inside * std.select(d.f32(0), d.f32(1), fp.brow.z > 0.002) * 0.72;
  return d.vec4f(std.mix(acc.xyz, INK, b), std.max(acc.w, b));
};

const dropLayer = (uv: d.v2f, c: d.v2f, size: number, amount: number, aa: number, acc: d.v4f): d.v4f => {
  'use gpu';
  const p = uv.sub(c);
  const body = std.length(p) - size;
  const tip = std.max(std.abs(p.x) * 1.8 + (p.y - size * 2.2) * 0.9, -p.y);
  const dd = std.min(body, tip);
  const fill = cover(dd, aa) * amount;
  let col = std.mix(acc.xyz, d.vec3f(0.62, 0.82, 0.96), fill);
  const line = strokeA(dd, 0.008, aa) * amount;
  col = std.mix(col, INK, line);
  return d.vec4f(col, std.max(acc.w, std.max(fill, line)));
};

/** All inked features at face-space uv (rgb + coverage). */
export const faceFeatures = (uv: d.v2f): d.v4f => {
  'use gpu';
  const fp = faceLayout.$.face;
  const fw = std.fwidth(uv);
  const aa = std.max(std.length(fw) * 0.75, 0.0015);
  let acc = d.vec4f(0, 0, 0, 0);
  // blush under the features
  for (const si of tgpu.unroll(std.range(2))) {
    const s = d.f32(si) * 2.0 - 1.0;
    const bp = uv.sub(d.vec2f(s * 0.62 * fp.eye.x, fp.eye.y - 0.4));
    const bk = std.length(bp.div(d.vec2f(0.24, 0.13)));
    const ba = (1.0 - std.smoothstep(0.2, 1.0, bk)) * fp.mouth2.w * 0.5;
    acc = d.vec4f(std.mix(acc.xyz, d.vec3f(0.93, 0.45, 0.45), ba), std.max(acc.w, ba));
  }
  acc = eyeLayer(uv, d.f32(1), aa, acc);
  acc = eyeLayer(uv, d.f32(-1), aa, acc);
  acc = browLayer(uv, d.f32(1), aa, acc);
  acc = browLayer(uv, d.f32(-1), aa, acc);
  // Preserve sculpted noses, but paint an animated mouth on the fitted muzzle.
  // nose: rounded inverted triangle + philtrum
  const ns = fp.brow2.z;
  const np = uv.sub(d.vec2f(0, -0.36 + fp.brow2.y));
  const nd = std.max(std.max(np.y - ns * 0.35, std.dot(np, d.vec2f(0.8, -0.6)) - ns * 0.45), std.dot(np, d.vec2f(-0.8, -0.6)) - ns * 0.45) - ns * 0.08;
  const na = cover(nd, aa)*std.select(d.f32(1),d.f32(0),fp.erase.z>.5);
  let col = std.mix(acc.xyz, fp.nose.xyz, na);
  const nl = strokeA(nd, 0.01, aa)*std.select(d.f32(1),d.f32(0),fp.erase.z>.5);
  col = std.mix(col, INK, nl);
  let a = std.max(acc.w, std.max(na, nl));
  // mouth
  const my = -0.62 + fp.mouth.x;
  const hw = fp.mouth.y * 0.5;
  const open = fp.mouth.z;
  const smile = fp.mouth2.x;
  const mp = uv.sub(d.vec2f(0, my));
  const xn = std.clamp(mp.x / hw, -1.0, 1.0);
  const phil = strokeA(mp.x, 0.008, aa) * std.smoothstep(0.0, 0.02, mp.y) * (1.0 - std.smoothstep(-0.36 + fp.brow2.y - ns * 0.7 - my - 0.02, -0.36 + fp.brow2.y - ns * 0.7 - my, mp.y));
  col = std.mix(col, INK, phil);
  a = std.max(a, phil);
  // closed: the cat "ω", corners lifted by the smile
  const wv = -0.045 * std.abs(std.sin(xn * 3.14159265)) + smile * 0.16 * xn * xn;
  const inMouthX = 1.0 - std.smoothstep(hw * 0.96, hw * 1.04, std.abs(mp.x));
  const closedA = strokeA(mp.y - wv, 0.013, aa) * inMouthX * (1.0 - std.smoothstep(0.04, 0.14, open));
  col = std.mix(col, INK, closedA);
  a = std.max(a, closedA);
  // open: a rounded hole with a flat-ish top, tongue and fangs
  const oh = std.max(open * 0.2, 0.001);
  const top = 0.015 + smile * 0.06 * xn * xn;
  const hole = std.max(ellipse(mp.sub(d.vec2f(0, top - oh)), d.vec2f(hw * (1.0 - fp.mouth.w * 0.35), oh)), mp.y - top);
  const holeA = cover(hole, aa) * std.smoothstep(0.03, 0.1, open);
  col = std.mix(col, d.vec3f(0.27, 0.07, 0.07), holeA);
  const tongueA = cover(std.max(std.length(mp.sub(d.vec2f(0, top - oh * 2.0))) - hw * 0.55, hole), aa) * std.smoothstep(0.03, 0.1, open) * std.max(0.6, fp.mouth2.z);
  col = std.mix(col, d.vec3f(0.86, 0.42, 0.44), tongueA);
  for (const si of tgpu.unroll(std.range(2))) {
    const s = d.f32(si) * 2.0 - 1.0;
    const fx = s * hw * 0.42;
    const fh = 0.075 * fp.mouth2.y;
    const tp = mp.sub(d.vec2f(fx, top));
    const fang = std.max(std.max(tp.y, -fh - tp.y), std.abs(tp.x) - 0.03 * (tp.y + fh) / std.max(fh, 0.001));
    const fa = cover(fang, aa) * std.select(d.f32(0), d.f32(1), fp.mouth2.y > 0.05) * std.smoothstep(0.0, 0.06, open + fp.mouth2.y * 0.2);
    col = std.mix(col, d.vec3f(0.97, 0.95, 0.86), fa);
    col = std.mix(col, INK, strokeA(fang, 0.005, aa) * fa);
    a = std.max(a, fa);
  }
  const holeLine = strokeA(hole, 0.012, aa) * std.smoothstep(0.03, 0.1, open);
  col = std.mix(col, INK, holeLine);
  a = std.max(a, std.max(holeA, holeLine));
  // whisker dots
  for (const si of tgpu.unroll(std.range(2))) {
    const s = d.f32(si) * 2.0 - 1.0;
    for (const ki of tgpu.unroll(std.range(3))) {
      const k = d.f32(ki);
      const dp = uv.sub(d.vec2f(s * (0.17 + k * 0.08), my + 0.13 + k * 0.035 - k * k * 0.012));
      const da = cover(std.length(dp) - 0.016, aa) * fp.brow2.w * 0.85;
      col = std.mix(col, INK, da);
      a = std.max(a, da);
    }
  }
  acc = d.vec4f(col, a);
  // tear (falls slowly) and sweat
  const ft = std.fract(fp.erase.y * 0.35);
  acc = dropLayer(uv, d.vec2f(0.55 * fp.eye.x, fp.eye.y - fp.eye.z * fp.eye.w - 0.08 - ft * 0.35), 0.045, fp.misc.x * (1.0 - std.smoothstep(0.7, 1.0, ft)), aa, acc);
  acc = dropLayer(uv, d.vec2f(-0.88 * fp.eye.x, fp.eye.y + 0.42), 0.04, fp.misc.y, aa, acc);
  // debug: the erase ellipse and the frame axes
  if (fp.misc.w > 0.5) {
    const k = std.length(uv.sub(d.vec2f(0, -0.28 + fp.erase.x)).div(d.vec2f(1.18 * fp.nose.w, 0.98 * fp.nose.w)));
    const dl = strokeA(k - 1.0, 0.01, aa) + strokeA(uv.x, 0.004, aa) + strokeA(uv.y, 0.004, aa);
    acc = d.vec4f(std.mix(acc.xyz, d.vec3f(0.9, 0.1, 0.1), std.min(dl, 1.0)), std.max(acc.w, std.min(dl, 1.0)));
  }
  return acc;
};
